import { getAccessToken } from '../authentication/supabaseService';
import type { RevenueRange } from '../demoService';

const API_BASE = '/api/admin';

// ── Types (mirror server/routes/superAdmin/demoLinksRoutes.ts serializers) ──
// Timestamps arrive as ISO strings: the server's Date fields are JSON-serialized.

export interface DemoPrefillSummary {
  id: string;
  ref: string;
  /** Full shareable URL, built server-side from DEMO_PAGE_BASE_URL. */
  url: string;
  businessName: string | null;
  restaurantCount: number | null;
  restaurantCount20Plus: boolean;
  revenueRange: RevenueRange | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  /** Set on the first booking that used this link. */
  usedAt: string | null;
}

export type DemoRequestStatus = 'booked' | 'below_threshold';

export interface DemoRequestSummary {
  id: string;
  createdAt: string;
  status: DemoRequestStatus;
  businessName: string | null;
  restaurantCount: number | null;
  restaurantCount20Plus: boolean;
  revenueRange: RevenueRange | null;
  challenges: string[];
  contactName: string | null;
  email: string;
  phone: string | null;
  locale: string | null;
  prefillId: string | null;
  meetingStartTime: string | null;
  calendlyCancelUrl: string | null;
  calendlyRescheduleUrl: string | null;
}

export interface CreateDemoPrefillRequest {
  slug: string;
  businessName?: string | null;
  restaurantCount?: number | null;
  restaurantCount20Plus?: boolean;
  revenueRange?: RevenueRange | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

/** Edit payload — every editable field, always sent whole (the server replaces, never patches). */
export type UpdateDemoPrefillRequest = Omit<CreateDemoPrefillRequest, 'slug'>;

export interface CreatedDemoPrefill {
  ref: string;
  url: string;
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    // The demo-prefill validation errors carry a useful `error` message — surface it.
    const message = await response
      .json()
      .then((body: { error?: string }) => body?.error)
      .catch(() => undefined);
    throw new Error(message ?? `Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  return response.json();
}

/** Creates a prefilled `/book-demo?ref=…` link; the server appends the random suffix to the slug. */
export async function createDemoPrefill(payload: CreateDemoPrefillRequest): Promise<CreatedDemoPrefill> {
  return fetchWithAuth('/demo-prefills', { method: 'POST', body: JSON.stringify(payload) });
}

/** Every prefill link, newest first. */
export async function getDemoPrefills(): Promise<DemoPrefillSummary[]> {
  const data = (await fetchWithAuth('/demo-prefills')) as { prefills: DemoPrefillSummary[] };
  return data.prefills;
}

/** Every captured lead — booked demos and below-threshold sign-ups — newest first. */
export async function getDemoRequests(): Promise<DemoRequestSummary[]> {
  const data = (await fetchWithAuth('/demo-requests')) as { requests: DemoRequestSummary[] };
  return data.requests;
}

/**
 * Edits a link's prefilled answers. The `ref`/URL is immutable server-side — it has usually been
 * sent already — so editing only changes what a FUTURE visit prefills.
 */
export async function updateDemoPrefill(
  id: string,
  payload: UpdateDemoPrefillRequest
): Promise<DemoPrefillSummary> {
  return fetchWithAuth(`/demo-prefills/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
}

/** Retires a link. Leads captured through it survive and only lose their prefill attribution. */
export async function deleteDemoPrefill(id: string): Promise<void> {
  await fetchWithAuth(`/demo-prefills/${id}`, { method: 'DELETE' });
}

/** ⚠️ Irreversible, and it does NOT cancel the Calendly meeting — only removes the stored lead. */
export async function deleteDemoRequest(id: string): Promise<void> {
  await fetchWithAuth(`/demo-requests/${id}`, { method: 'DELETE' });
}
